import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Star,
  Award,
  User,
  ThumbsUp,
  LayoutGrid,
  ArrowRight,
} from 'lucide-react';
import { Container } from '../../components/styled/Container';
import { PageLayout } from '../../components/styled/PageLayout';
import { fetchMediaDetail } from '../../store/actions/mediaActions';
import { fetchReviews, submitReview } from '../../store/actions/reviewActions';
import { fetchSimilarList } from '../../store/actions/similarActions';
import { similarPath, parseIdSlug } from '../../utils/paths';
import { buildMediaDetailJsonLd } from '../../utils/mediaDetailJsonLd';
import { buildMediaSeo } from '../../utils/pageSeo';
import { pathWithRegion } from '../../utils/navBase';
import { regionFromPathname } from '../../utils/regionFromPath';
import { serverLanguageFromRegion, watchRegionFromSiteRegion } from '../../utils/serverLanguage';
import SEO from '../../components/seo/SEO';
import { useI18n } from '../../hooks/useI18n';
import { useAdDevice } from '../../hooks/useAdDevice';
import StaticAdBanner from '../../components/ads/StaticAdBanner';
import AdSidebar from '../../components/ads/AdSidebar';
import PromoAdCard from '../../components/ads/PromoAdCard';
import { bannerAds, sidebarAds, cardAds } from '../../data/staticAds';
import { useSiteAdsManaged } from '../../hooks/useSiteAdsManaged';
import { fetchAdPlacement, trackAdClick, trackAdImpression } from '../../store/actions/adActions';
import { selectAdPlacementFirst, selectAdPlacementList } from '../../store/selectors/adSelectors';
import { managedPlacementToAd, pickStaticFallback } from '../../utils/adMapper';
import { makeImpressionKey, shouldTrackImpression } from '../../utils/adTracking';
import { selectAdsStaticEnabled } from '../../store/reducers/sitePublicReducer';
import MediaOffers from '../../components/media/MediaOffers';
import MediaDetailExtras from '../../components/media/MediaDetailExtras';
import RichContent from '../../components/content/RichContent';
import CrawlerSeoContent, { stripHtml } from '../../components/seo/CrawlerSeoContent';
import { escapeHtml, extractFaqJsonLd } from '../../utils/richContent';

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin-bottom: 1.5rem;
  text-decoration: none;
  &:hover {
    color: ${({ theme }) => theme.colors.foreground};
  }
`;

const Card = styled(motion.div)`
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.radiusSm};
  box-shadow: ${({ theme }) => theme.shadowCard};
  overflow: hidden;
`;

const AccentBar = styled.div`
  height: 6px;
  background: ${({ theme }) => theme.colors.primary};
`;

const CardInner = styled.div`
  padding: 2rem;
  @media (min-width: 768px) {
    padding: 2.5rem;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
  }
`;

const LogoLarge = styled.div`
  width: 5rem;
  height: 5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: rgba(0, 48, 135, 0.1);
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  flex-shrink: 0;
`;

const HeaderInfo = styled.div`
  flex: 1;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SiteTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontClamp['3xl']};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const ScoreBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  background: rgba(234, 179, 8, 0.1);
  color: ${({ theme }) => theme.colors.gold};
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const ShortDescription = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.mutedForeground};
  margin: 0.75rem 0 0 0;
  max-width: 42rem;
  line-height: 1.6;
`;

const ViewSimilarBtn = styled(Link)`
  padding: 0.75rem 1.5rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.9;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 2rem;
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const StatBox = styled.div`
  padding: 1rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ $bg }) => $bg};
`;

const StatLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: ${({ $color }) => $color};
`;

const StatValue = styled.p`
  font-variant-numeric: tabular-nums;
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
`;

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Tag = styled.span`
  padding: 0.375rem 1rem;
  background: rgba(220, 38, 38, 0.1);
  color: ${({ theme }) => theme.colors.crimson};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  font-weight: 500;
  border-radius: ${({ theme }) => theme.radiusSm};
`;

const SectionHeading = styled.h2`
  font-size: ${({ theme }) => theme.fontClamp.xl};
  font-weight: 700;
  color: ${({ theme }) => theme.colors.foreground};
  margin: 2rem 0 0.75rem 0;
`;

const SeoBodySection = styled(RichContent)`
  margin-top: 0.25rem;
`;

const ReviewCard = styled.div`
  padding: 1rem 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:last-child {
    border-bottom: none;
  }
`;

const ReviewMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const ReviewText = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.foreground};
  margin: 0;
  line-height: 1.5;
`;

const ReviewAuthorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 0.35rem;
`;

const ReviewAuthorAvatar = styled.div`
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ theme }) => theme.colors.muted};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ReviewAuthorLink = styled(Link)`
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ReviewAuthorPlain = styled.span`
  font-weight: 700;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: ${({ theme }) => theme.colors.foreground};
`;

const ReviewForm = styled.form`
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const StarBtn = styled.button`
  background: none;
  padding: 0;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.mutedForeground};
  &:hover,
  &.filled {
    color: ${({ theme }) => theme.colors.gold};
  }
`;

const ReviewTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.muted};
  font-size: ${({ theme }) => theme.fontClamp.sm};
  resize: vertical;
  outline: none;
  border: 2px solid transparent;
  font-family: inherit;
  color: ${({ theme }) => theme.colors.foreground};
  &:focus {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.primary};
  }
`;

const ReviewSubmitBtn = styled.button`
  align-self: flex-start;
  padding: 0.5rem 1.25rem;
  border-radius: ${({ theme }) => theme.radiusSm};
  background: ${({ theme }) => theme.colors.crimson};
  color: ${({ theme }) => theme.colors.crimsonForeground};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontClamp.sm};
  border: none;
  cursor: pointer;
  &:hover {
    opacity: 0.9;
  }
`;

const SuccessMsg = styled.p`
  font-size: ${({ theme }) => theme.fontClamp.sm};
  color: hsl(142, 71%, 45%);
  font-weight: 600;
  margin: 0;
`;

function kindLabel(category, t) {
  if (category === 'anime_tv') return t('media.detail.kind.animeTv', 'Anime TV series');
  if (category === 'anime_movie') return t('media.detail.kind.animeMovie', 'Anime movie');
  if (category === 'tv') return t('media.detail.kind.tv', 'TV series');
  return t('media.detail.kind.movie', 'Movie');
}

function similarCtaLabel(category, t) {
  if (category === 'anime_movie' || category === 'anime_tv') {
    return t('media.detail.cta.similarAnime', 'View similar anime');
  }
  if (category === 'tv' || category === 'anime_tv') {
    return t('media.detail.cta.similarTv', 'View similar TV shows');
  }
  return t('media.detail.cta.similarMovies', 'View similar movies');
}

function ReviewAuthorBlock({ author, region, t }) {
  if (!author) return null;
  const profileTo = author.profileUsername ? pathWithRegion(`/profile/${author.profileUsername}`, region) : null;
  return (
    <ReviewAuthorRow>
      <ReviewAuthorAvatar aria-hidden>
        {author.avatarUrl ? <img src={author.avatarUrl} alt="" /> : <User size={17} style={{ opacity: 0.38 }} />}
      </ReviewAuthorAvatar>
      {profileTo ? (
        <ReviewAuthorLink to={profileTo}>{author.label}</ReviewAuthorLink>
      ) : (
        <ReviewAuthorPlain title={t('media.detail.reviewAuthorPrivate', 'This member’s profile is private.')}>
          {author.label}
        </ReviewAuthorPlain>
      )}
    </ReviewAuthorRow>
  );
}

export default function MediaDetailPage({ category }) {
  const { idSlug } = useParams();
  const { id, slug } = useMemo(() => parseIdSlug(idSlug), [idSlug]);
  const baseId = useMemo(() => parseInt(id, 10), [id]);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const region = regionFromPathname(pathname);
  const pre = region || '';
  const { t, locale } = useI18n();
  const serverLanguage = serverLanguageFromRegion(region);
  const watchRegion = watchRegionFromSiteRegion(region);

  const adDomain = useMemo(() => (typeof window !== 'undefined' ? window.location.hostname : ''), []);
  const adLocale = (locale || 'en').toLowerCase();
  const adDevice = useAdDevice();
  const { sitePublicLoaded, adsManagedEnabled } = useSiteAdsManaged();
  const adsStaticEnabled = useSelector(selectAdsStaticEnabled);

  const managedDetailBanner = useSelector((s) => selectAdPlacementFirst(s, 'media-detail', 'inline-banner'));
  const managedDetailSidebars = useSelector((s) => selectAdPlacementList(s, 'media-detail', 'sidebar'));
  const managedDetailCard = useSelector((s) => selectAdPlacementFirst(s, 'media-detail', 'inline-card'));

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled) return;
    const baseParams = { device: adDevice, domain: adDomain, locale: adLocale };
    dispatch(
      fetchAdPlacement({
        page: 'media-detail',
        placement: 'inline-banner',
        params: { ...baseParams, page: 'media-detail', placement: 'inline-banner', limit: 1 },
      }),
    );
    dispatch(
      fetchAdPlacement({
        page: 'media-detail',
        placement: 'sidebar',
        params: { ...baseParams, page: 'media-detail', placement: 'sidebar', limit: 2 },
      }),
    );
    dispatch(
      fetchAdPlacement({
        page: 'media-detail',
        placement: 'inline-card',
        params: { ...baseParams, page: 'media-detail', placement: 'inline-card', limit: 1 },
      }),
    );
  }, [dispatch, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedDetailBanner?._id) return;
    const key = makeImpressionKey({
      campaign: managedDetailBanner._id,
      page: 'media-detail',
      placement: 'inline-banner',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedDetailBanner._id,
          page: 'media-detail',
          placement: 'inline-banner',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedDetailBanner, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled) return;
    managedDetailSidebars.forEach((item) => {
      if (!item?._id) return;
      const key = makeImpressionKey({
        campaign: item._id,
        page: 'media-detail',
        placement: 'sidebar',
        device: adDevice,
        domain: adDomain,
      });
      if (shouldTrackImpression(key)) {
        dispatch(
          trackAdImpression({
            campaign: item._id,
            page: 'media-detail',
            placement: 'sidebar',
            device: adDevice,
            domain: adDomain,
            locale: adLocale,
          }),
        );
      }
    });
  }, [dispatch, managedDetailSidebars, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  useEffect(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedDetailCard?._id) return;
    const key = makeImpressionKey({
      campaign: managedDetailCard._id,
      page: 'media-detail',
      placement: 'inline-card',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedDetailCard._id,
          page: 'media-detail',
          placement: 'inline-card',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedDetailCard, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const detailBannerAd = useMemo(
    () => managedPlacementToAd(managedDetailBanner, pickStaticFallback(adsStaticEnabled, bannerAds[0])),
    [managedDetailBanner, adsStaticEnabled],
  );
  const detailSidebarLeft = useMemo(
    () => managedPlacementToAd(managedDetailSidebars[0], pickStaticFallback(adsStaticEnabled, sidebarAds[0])),
    [managedDetailSidebars, adsStaticEnabled],
  );
  const detailSidebarRight = useMemo(
    () =>
      managedPlacementToAd(
        managedDetailSidebars[1],
        pickStaticFallback(adsStaticEnabled, sidebarAds[1] ?? sidebarAds[0]),
      ),
    [managedDetailSidebars, adsStaticEnabled],
  );
  const detailPromoAd = useMemo(
    () => managedPlacementToAd(managedDetailCard, pickStaticFallback(adsStaticEnabled, cardAds[0])),
    [managedDetailCard, adsStaticEnabled],
  );

  const onDetailBannerClick = useCallback(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedDetailBanner?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedDetailBanner._id,
        page: 'media-detail',
        placement: 'inline-banner',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedDetailBanner, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const onDetailSidebarClick = useCallback(
    (campaignId) => () => {
      if (!sitePublicLoaded || !adsManagedEnabled || !campaignId) return;
      dispatch(
        trackAdClick({
          campaign: campaignId,
          page: 'media-detail',
          placement: 'sidebar',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    },
    [dispatch, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled],
  );

  const onDetailPromoClick = useCallback(() => {
    if (!sitePublicLoaded || !adsManagedEnabled || !managedDetailCard?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedDetailCard._id,
        page: 'media-detail',
        placement: 'inline-card',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedDetailCard, adDevice, adDomain, adLocale, sitePublicLoaded, adsManagedEnabled]);

  const { detail: hero, loading, error } = useSelector((s) => s.media);
  const { list: reviews, loading: revLoading } = useSelector((s) => s.reviews);
  const { results: similarResults } = useSelector((s) => s.similar);

  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [reviewMsg, setReviewMsg] = useState('');

  useEffect(() => {
    if (!Number.isFinite(baseId)) return;
    dispatch(fetchMediaDetail(category, baseId, { language: serverLanguage, watchRegion }));
    dispatch(fetchReviews(category, baseId));
    dispatch(fetchSimilarList(category, baseId, { language: serverLanguage }));
  }, [dispatch, category, baseId, serverLanguage, watchRegion]);

  const onReview = async (e) => {
    e.preventDefault();
    if (rating < 1) return;
    const res = await dispatch(submitReview(category, baseId, { rating, text, title }));
    if (res.success) {
      setRating(0);
      setText('');
      setReviewMsg(t('media.detail.reviewPosted', 'Thanks! Your review was posted.'));
    } else {
      setReviewMsg(res.message || t('media.detail.reviewFailed', 'Could not post review'));
    }
  };

  const similarTo = hero
    ? similarPath(category, baseId, hero.title, pre)
    : '#';
  const homeTo = pre ? `/${pre}` : '/';
  const title = hero?.title || `#${baseId}`;
  const tmdbScore = hero?.voteAverage != null ? Number(hero.voteAverage).toFixed(1) : null;
  const reviewCount = Array.isArray(reviews) ? reviews.length : 0;
  const reviewAvg = useMemo(() => {
    if (!reviewCount) return null;
    const sum = reviews.reduce((acc, r) => acc + (Number(r?.rating) || 0), 0);
    return (sum / reviewCount).toFixed(1);
  }, [reviews, reviewCount]);
  const voteStats = useMemo(() => {
    const rows = Array.isArray(similarResults) ? similarResults : [];
    let weighted = 0;
    let totalVotes = 0;
    rows.forEach((s) => {
      const avg = Number(s?.similarityVoteStats?.averageRating || 0);
      const count = Number(s?.similarityVoteStats?.voteCount || 0);
      if (count > 0) {
        weighted += avg * count;
        totalVotes += count;
      }
    });
    const voteScore = totalVotes > 0 ? (weighted / totalVotes).toFixed(1) : null;
    return { voteScore, totalVotes };
  }, [similarResults]);
  const year = hero?.releaseDate ? String(hero.releaseDate).slice(0, 4) : '';
  const runtimeMinutes = hero?.runtime || hero?.episodeRunTime;
  const runtimeLabel = useMemo(() => {
    const m = Number(runtimeMinutes);
    if (!Number.isFinite(m) || m <= 0) return '';
    const h = Math.floor(m / 60);
    const r = m % 60;
    if (h > 0 && r > 0) return `${h}h ${r}m`;
    if (h > 0) return `${h}h`;
    return `${r}m`;
  }, [runtimeMinutes]);
  const defaultAbout = t(
    'media.detail.defaultAbout',
    'Discover details, reviews, and similar picks for this title.',
  );
  const pageSeo = buildMediaSeo({
    hero,
    category,
    baseId,
    regionPrefix: pre,
    fallbackTitleSuffix: t('media.detail.details', 'Details'),
    fallbackDescription: defaultAbout,
  });
  const aboutHtml = hero?.content && String(hero.content).trim()
    ? String(hero.content)
    : escapeHtml(hero?.overview || defaultAbout);
  const detailContentHtml =
    hero?.content && String(hero.content).trim() ? String(hero.content).trim() : '';
  const showDetailSeoBody = useMemo(() => {
    if (!detailContentHtml) return false;
    const bodyPlain = stripHtml(detailContentHtml);
    const overviewPlain = stripHtml(hero?.overview || '');
    return bodyPlain.length > 0 && bodyPlain !== overviewPlain;
  }, [detailContentHtml, hero?.overview]);
  const faqJsonLd = useMemo(() => extractFaqJsonLd(aboutHtml), [aboutHtml]);
  const movieJsonLd = useMemo(
    () =>
      buildMediaDetailJsonLd({
        category,
        pageTitle: pageSeo.title,
        pageDescription: pageSeo.description,
        pageImage: pageSeo.image,
        hero,
        region,
        tmdbScore,
        reviewCount,
        reviewAvg,
        runtimeMinutes,
      }),
    [
      category,
      pageSeo.title,
      pageSeo.description,
      pageSeo.image,
      hero,
      region,
      tmdbScore,
      reviewCount,
      reviewAvg,
      runtimeMinutes,
    ],
  );

  if (!Number.isFinite(baseId)) {
    return (
      <PageLayout>
        <Container>
          <p>{t('common.invalidTitle', 'Invalid title.')}</p>
        </Container>
      </PageLayout>
    );
  }

  if (loading && !hero) {
    return (
      <PageLayout>
        <Container style={{ paddingTop: '5rem' }}>
          <p style={{ color: 'var(--muted-foreground)' }}>{t('common.loading', 'Loading...')}</p>
        </Container>
      </PageLayout>
    );
  }

  if (error && !hero) {
    return (
      <PageLayout>
        <Container style={{ paddingTop: '5rem' }}>
          <BackLink to={homeTo}>
            <ArrowLeft size={16} strokeWidth={2.5} /> {t('common.home', 'Home')}
          </BackLink>
          <p>{error}</p>
        </Container>
      </PageLayout>
    );
  }

  return (
    <>
      <SEO
        region={region}
        path={pageSeo.path}
        canonicalOverride={pageSeo.canonicalOverride}
        title={pageSeo.title}
        description={pageSeo.description}
        keywords={pageSeo.keywords}
        noindex={pageSeo.noindex}
        image={pageSeo.image}
        jsonLd={[movieJsonLd, faqJsonLd].filter(Boolean)}
        hreflangAlternates={pageSeo.hreflangAlternates}
      />
      <CrawlerSeoContent title={pageSeo.title} intro={hero?.overview || pageSeo.description} bodyParagraphs={[aboutHtml]} />
      <PageLayout>
        <Container style={{ paddingTop: '2.5rem', paddingBottom: '2.5rem' }}>
        <BackLink to={homeTo}>
          <ArrowLeft size={16} strokeWidth={2.5} /> {t('common.home', 'Home')}
        </BackLink>

        <Card initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <AccentBar />
          <CardInner>
            <HeaderRow>
              <LogoLarge
                $url={hero?.posterUrl}
                role={hero?.posterUrl ? 'img' : undefined}
                aria-label={hero?.posterUrl ? hero?.posterAlt || title : undefined}
              >
                {!hero?.posterUrl ? title.charAt(0) : ''}
              </LogoLarge>
              <HeaderInfo>
                <TitleRow>
                  <SiteTitle>{title}</SiteTitle>
                  {hero?.certification ? (
                    <ScoreBadge style={{ background: 'rgba(0,48,135,0.08)', color: 'var(--primary)' }}>
                      {hero.certification}
                    </ScoreBadge>
                  ) : null}
                  {tmdbScore != null && (
                    <ScoreBadge>
                      <Star size={12} fill="currentColor" /> TMDB {tmdbScore}/10
                    </ScoreBadge>
                  )}
                  {voteStats.voteScore != null && (
                    <ScoreBadge>
                      <Star size={12} fill="currentColor" /> {voteStats.voteScore}/5 {t('common.votes', 'votes')}
                    </ScoreBadge>
                  )}
                </TitleRow>
                {hero?.originalTitle && hero.originalTitle !== title ? (
                  <p
                    style={{
                      margin: '0.15rem 0 0',
                      fontSize: '0.8125rem',
                      color: 'var(--muted-foreground)',
                    }}
                  >
                    {hero.originalTitle}
                  </p>
                ) : null}
                <p
                  style={{
                    margin: '0.25rem 0 0',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    color: 'var(--primary)',
                  }}
                >
                  {kindLabel(category, t)}
                  {year ? ` · ${year}` : ''}
                  {runtimeLabel ? ` · ${runtimeLabel}` : ''}
                </p>
                <ShortDescription>{hero?.overview || t('common.noOverview', 'No overview.')}</ShortDescription>
              </HeaderInfo>
              <ViewSimilarBtn to={similarTo}>
                {similarCtaLabel(category, t)} <ArrowRight size={14} />
              </ViewSimilarBtn>
            </HeaderRow>

            <StatsGrid>
              <StatBox $bg="rgba(128,0,0,0.05)">
                <StatLabel $color="var(--maroon)">
                  <LayoutGrid size={14} strokeWidth={2.5} /> {t('common.type', 'Type')}
                </StatLabel>
                <StatValue style={{ fontSize: '1.25rem' }}>{kindLabel(category, t)}</StatValue>
              </StatBox>
              <StatBox $bg="rgba(234,179,8,0.05)">
                <StatLabel $color="var(--primary)">
                  <ThumbsUp size={14} strokeWidth={2.5} /> {t('common.voteScore', 'Vote score')}
                </StatLabel>
                <StatValue style={{ fontSize: '1rem' }}>
                  {voteStats.voteScore != null
                    ? `${voteStats.voteScore}/5 (${voteStats.totalVotes} ${t('common.votes', 'votes')})`
                    : t('common.noVotes', 'No votes yet')}
                </StatValue>
              </StatBox>
              <StatBox $bg="rgba(34,197,94,0.08)">
                <StatLabel $color="hsl(142,71%,45%)">
                  <Award size={14} strokeWidth={2.5} /> {t('common.reviewRating', 'Review rating')}
                </StatLabel>
                <StatValue style={{ fontSize: '1rem' }}>
                  {reviewAvg != null
                    ? `${reviewAvg}/5 (${reviewCount} ${t('common.reviews', 'reviews')})`
                    : t('common.noReviews', 'No reviews yet')}
                </StatValue>
              </StatBox>
            </StatsGrid>

            <TagsWrap>
              {(hero?.genres || []).map((g) => (
                <Tag key={g.id}>{g.name}</Tag>
              ))}
            </TagsWrap>
          </CardInner>
        </Card>

        <div style={{ marginTop: '1.5rem' }}>
          <StaticAdBanner ad={detailBannerAd} variant="inline" pageKey="media-detail" onLinkClick={onDetailBannerClick} />
        </div>
        <div style={{ marginTop: '1.5rem' }}>
          <MediaOffers
            offers={hero?.offers}
            category={category}
            tmdbId={baseId}
          />
        </div>
        <div
          style={{
            marginTop: '1.5rem',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
            gap: '1rem',
            alignItems: 'start',
          }}
        >
          <AdSidebar
            ad={detailSidebarLeft}
            pageKey="media-detail"
            onLinkClick={onDetailSidebarClick(detailSidebarLeft?.managedCampaignId)}
          />
          <AdSidebar
            ad={detailSidebarRight}
            pageKey="media-detail"
            onLinkClick={onDetailSidebarClick(detailSidebarRight?.managedCampaignId)}
          />
        </div>
        <div style={{ marginTop: '1.5rem', maxWidth: '40rem' }}>
          <PromoAdCard ad={detailPromoAd} pageKey="media-detail" onLinkClick={onDetailPromoClick} />
        </div>

        <MediaDetailExtras
          hero={hero}
          category={category}
          t={t}
          mediaId={baseId}
          language={serverLanguage}
          locale={locale}
          showTitle={title}
        />

        {showDetailSeoBody ? (
          <>
            <SectionHeading>{t('media.detail.aboutTitle', 'About this title')}</SectionHeading>
            <SeoBodySection html={detailContentHtml} />
          </>
        ) : null}

        <Card initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: '2rem' }}>
          <AccentBar />
          <CardInner>
            <SectionHeading style={{ marginTop: 0 }}>{t('common.reviews', 'Reviews')}</SectionHeading>
            {reviewMsg && <SuccessMsg>{reviewMsg}</SuccessMsg>}
            <ReviewForm onSubmit={onReview}>
              <StarRow>
                {[1, 2, 3, 4, 5].map((n) => (
                  <StarBtn
                    key={n}
                    type="button"
                    className={rating >= n ? 'filled' : ''}
                    onClick={() => setRating(n)}
                    aria-label={`${n} star`}
                  >
                    <Star size={24} fill={rating >= n ? 'currentColor' : 'none'} />
                  </StarBtn>
                ))}
              </StarRow>
              <ReviewTextarea
                placeholder={t('media.detail.reviewPlaceholder', 'Your review (optional)')}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <ReviewSubmitBtn type="submit">{t('media.detail.submitReview', 'Submit review')}</ReviewSubmitBtn>
            </ReviewForm>
            {revLoading && !reviews.length ? (
              <p style={{ color: 'var(--muted-foreground)' }}>{t('media.detail.loadingReviews', 'Loading reviews...')}</p>
            ) : (
              reviews.map((r) => (
                <ReviewCard key={r._id}>
                  <ReviewAuthorBlock author={r.author} region={region} t={t} />
                  <ReviewMeta>
                    <Star size={12} fill="currentColor" style={{ color: 'var(--gold)' }} />
                    {r.rating}/5 · {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : ''}
                  </ReviewMeta>
                  <ReviewText>{r.text || '—'}</ReviewText>
                </ReviewCard>
              ))
            )}
          </CardInner>
        </Card>
        </Container>
      </PageLayout>
    </>
  );
}
